import {
  faChevronDown,
  faCheck,
  faSpinner,
} from "@fortawesome/pro-regular-svg-icons";
import React from "react";
import { twJoin } from "tailwind-merge";

import { RenderValueFn } from "src/base-components/DropDown";
import {
  FixedPositionedDropdown,
  FixedPositionedDropdownProps,
} from "src/base-components/FixedPositionedDropDown";
import { Icon } from "src/base-components/Icon";
import { SimpleDropDownProps } from "src/base-components/SimpleDropDown";
import { Tooltip } from "src/design-system/Tooltip";

// Requres refactor
// We're using types from two different components here
// This messes up the typing of the component
// The only way to keep it working is to use `any`
type Props = Omit<
  SimpleDropDownProps,
  "className" | "buttonClassName" | "itemsClassNames" | "placement"
> &
  Pick<
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    FixedPositionedDropdownProps<React.ReactNode, string, any>,
    | "placementOffsetX"
    | "placement"
    | "portal"
    | "sections"
    | "sectionDividers"
    | "buttonClassName"
  >;

export const FixedSimpleDropdown: React.FC<Props> = ({
  onSelect,
  elements,
  selectedKey,
  placeholder,
  buttonDataLoc,
  dataLoc,
  placement,
  itemsWidth = "w-56",
  loading = false,
  ...props
}) => {
  const selectedItem = elements.find((element) => element.key === selectedKey);
  const renderValue: RenderValueFn<string> = ({
    value,
    selected,
    icon,
    disabled,
    helpText,
    helpBody,
  }) => (
    <Tooltip
      body={helpBody}
      disabled={typeof helpText !== "string"}
      placement="right"
      title={helpText}
      asChild
    >
      <div
        className={twJoin(
          "flex gap-x-2 border-y border-y-transparent",
          itemsWidth,
          disabled && "cursor-default",
        )}
        data-loc={`select-${value}`}
      >
        {icon && <Icon color="text-gray-500" icon={icon} size="xs" />}
        <span
          className={twJoin("overflow-hidden", disabled && "text-gray-500")}
        >
          {value}
        </span>
        {selected && (
          <span className="ml-auto">
            <Icon color="text-indigo-500" icon={faCheck} size="sm" />
          </span>
        )}
      </div>
    </Tooltip>
  );

  const renderButtonValue = (value?: string) => (
    <div className="flex w-full items-center gap-x-1 py-1.5 pl-3 pr-2">
      {selectedItem?.icon && (
        <Icon
          color={props.disabled ? "text-gray-300" : "text-gray-500"}
          icon={selectedItem.icon}
          size="xs"
        />
      )}
      <div
        className={twJoin(
          "min-w-0 grow truncate text-left font-inter-normal-12px",
          props.disabled ? "text-gray-500" : "text-gray-800",
        )}
      >
        {value ? value : placeholder}
      </div>
      {(loading || elements.length > 0) && (
        <div className="ml-auto flex h-auto items-center">
          <Icon
            color="text-gray-500"
            icon={loading ? faSpinner : faChevronDown}
            size="xs"
            spin={loading}
          />
        </div>
      )}
    </div>
  );

  return (
    <FixedPositionedDropdown
      buttonDataLoc={buttonDataLoc}
      dataLoc={dataLoc}
      elements={elements}
      placement={placement}
      renderButtonValue={renderButtonValue}
      renderValue={renderValue}
      selected={selectedKey}
      onSelect={onSelect}
      {...props}
    />
  );
};
